
import { Component, OnInit, Input, ViewChild } from '@angular/core';
import { Validators, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { LocalStorageService } from 'ngx-webstorage';
import { QueryBuilderConfig, QueryBuilderClassNames } from '../../shared/query-builder/query-builder.interfaces';
import { Subject } from 'rxjs';
import { NGXLogger } from 'ngx-logger';
import { FieldConfigurationService } from '../../core/service/field-configuration.service';
import { QueryBuilderService } from '../../shared/query-builder/query-builder.service';
import { TabStripComponent } from '@progress/kendo-angular-layout';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { RuleService } from 'src/app/core/service/rule.service';
import { Base64 } from 'js-base64';

@Component({
  selector: 'app-field-configuration-test',
  templateUrl: './field-configuration-test.component.html',
  styleUrls: ['./field-configuration-test.component.scss']
})
export class FieldConfigurationTestComponent implements OnInit
{
  @ViewChild('tabstrip') public tabstrip: TabStripComponent;

  @Input() public opening: Subject<boolean>;

  public testingForm: any;
  public testingDialogOpened = false;
  public query = {
    condition: 'and',
    rules: []
  };
  public config: QueryBuilderConfig;
  public classNames: QueryBuilderClassNames;

  private keyXML = 'XML';
  private keyXMLSave = 'XMLSave';
  private keyQuerySave = 'QuerySave';

  constructor(
    private fb: UntypedFormBuilder,
    private storageService: LocalStorageService,
    private logger: NGXLogger,
    private fieldConfigurationService: FieldConfigurationService,
    private queryBuilderService: QueryBuilderService,
    private ruleService: RuleService,
    private messageHandlerService: MessageHandlerService)
  {
    this.initForm(false);
  }

  /**
   * Getter for form control field xml.
   */
  public get xml(): UntypedFormControl
  {
    return this.testingForm.get('xml');
  }

  /**
   * Getter for form control field xml.
   */
  public get xmlSave(): UntypedFormControl
  {
    return this.testingForm.get('xmlSave');
  }

  public ngOnInit(): void
  {
    this.opening.subscribe({
      next: () =>
      {
        this.loadForm();
      }
    });
  }

  /**
   * Perform a Rule check.
   */
  public executeRuleTesting(e: Event): void
  {
    e.preventDefault();

    this.saveLocalStorage();
    this.testingForm.controls.json.setValue('');

    // Call check rule
    const based64XML = Base64.encode(this.xml.value);
    this.ruleService.convertRulesValueArrayToString(this.query);
    this.fieldConfigurationService.checkField({ xml: based64XML, query: this.query }).subscribe({
      next: (data) =>
      {
        this.ruleService.convertRulesValueStringToArray(this.query);
        this.testingForm.controls.json.setValue(data);
        setTimeout(() => { this.tabstrip.selectTab(2); });
      },
      error: error =>
      {
        this.messageHandlerService.displayError(error);
        setTimeout(() => { this.tabstrip.selectTab(2); });
      }
    });
  }

  /**
   * Close the testing dialog form.
   */
  public closeTestingForm(): void
  {
    this.testingDialogOpened = false;
  }

  /**
   * initForm get called from constructor to initialize the Reactive Forms and from loadForm when the dialog opens.
   */
  private initForm(loadFromStorage: boolean): void
  {
    let xml = '';
    let xmlSave = false;

    if (loadFromStorage)
    {
      xml = this.storageService.retrieve(this.keyXML) || '';
      xmlSave = this.storageService.retrieve(this.keyXMLSave) || false;
      const savedQuery = this.storageService.retrieve(this.keyQuerySave);
      if (savedQuery)
      {
        // This ensure that any saved conditions are still valid field.
        /*TODO: To cleanup must be recursive on rules. Cleanup disabled for now.
        savedQuery.rules = this.queryBuilderService.cleanupRules(savedQuery.rules, this.config.fields);*/
        this.query = savedQuery;
      }
    }
    this.testingForm = this.fb.group({
      xml: [xml, Validators.required],
      xmlSave: [xmlSave],
      json: null
    });
  }

  /**
   * loadForm get called everytime the form opens. This allow reloading the rules and XML from storage.
   */
  private loadForm(): void
  {
    this.fieldConfigurationService.getCachedFieldConfigurations().subscribe({
      next: (data) =>
      {
        this.fieldConfigurationService.getCodeSetCodesUsedByFieldConfigurations(data).subscribe({
          next: (codeSetCodes) =>
          {
            // Replacing options with loaded Code Sets
            this.fieldConfigurationService.setCodeSetCodesUsedByFieldConfigurations(data, codeSetCodes);

            this.config = this.queryBuilderService.transform(data);
            this.classNames = this.queryBuilderService.classNames();

            this.initForm(true);
            this.testingDialogOpened = true;
          },
          error: (errorResponse) =>
          {
            this.messageHandlerService.displayError(errorResponse);
          }
        });
      },
      error: error =>
      {
        this.messageHandlerService.displayError(error);
      }
    });
  }

  /**
   * Save storage properties. XML content and save switch value.
   */
  private saveLocalStorage(): void
  {
    // Handle storage
    this.storageService.store(this.keyXMLSave, this.xmlSave.value);
    this.storageService.store(this.keyQuerySave, this.query);
    if (this.xmlSave.value)
    {
      this.storageService.store(this.keyXML, this.xml.value);
    }
    else
    {
      this.storageService.clear(this.keyXML);
    }
  }

}
